import React, { useState } from 'react';
import './FontSyncModal.css';

/**
 * 🎨 FONT SYNC MODAL - Intelligentes Font-Syncing zwischen Space Settings und Hero Headers
 * 
 * Fragt beim Speichern von Space Typography:
 * - Sollen alle Fonts durch die neuen ersetzt werden?
 * - Auf welchem Level: aktuelle Page oder ganzer Space?
 */
const FontSyncModal = ({ 
    show, 
    onClose, 
    onConfirm, 
    oldFonts, 
    newFonts,
    isSpacePage = false 
}) => {
    const [syncLevel, setSyncLevel] = useState('page'); // 'page' or 'space'
    const [syncScope, setSyncScope] = useState('replace'); // 'replace' or 'keep'

    if (!show) return null;

    const handleConfirm = () => {
        onConfirm({
            shouldSync: syncScope === 'replace',
            level: syncLevel
        });
    };

    const handleSkip = () => {
        onConfirm({
            shouldSync: false,
            level: null
        });
    };

    return (
        <div className="font-sync-modal-overlay" onClick={onClose}>
            <div className="font-sync-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="font-sync-modal__header">
                    <div className="font-sync-modal__icon">
                        <i className="fas fa-sync-alt"></i>
                    </div>
                    <h2 className="font-sync-modal__title">Typography Update</h2>
                    <p className="font-sync-modal__subtitle">
                        Sync font changes with your Hero Headers?
                    </p>
                </div>

                {/* Scrollable Content */}
                <div className="font-sync-modal__content">
                    {/* Font Changes Preview */}
                    <div className="font-sync-modal__changes">
                        <h3 className="font-sync-modal__changes-title">Font Changes</h3>
                        <div className="font-sync-modal__changes-grid">
                            <div className="font-sync-modal__change-item">
                                <span className="font-sync-modal__change-label">H1 (Big Font)</span>
                                <div className="font-sync-modal__change-fonts">
                                    <span className="font-sync-modal__font-old" style={{ fontFamily: oldFonts?.globalBigFont }}>
                                        {oldFonts?.globalBigFont || 'None'}
                                    </span>
                                    <i className="fas fa-arrow-right"></i>
                                    <span className="font-sync-modal__font-new" style={{ fontFamily: newFonts?.globalBigFont }}>
                                        {newFonts?.globalBigFont}
                                    </span>
                                </div>
                            </div>
                            
                            <div className="font-sync-modal__change-item">
                                <span className="font-sync-modal__change-label">H2/H3/P (Small Font)</span>
                                <div className="font-sync-modal__change-fonts">
                                    <span className="font-sync-modal__font-old" style={{ fontFamily: oldFonts?.globalSmallFont }}>
                                        {oldFonts?.globalSmallFont || 'None'}
                                    </span>
                                    <i className="fas fa-arrow-right"></i>
                                    <span className="font-sync-modal__font-new" style={{ fontFamily: newFonts?.globalSmallFont }}>
                                        {newFonts?.globalSmallFont}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sync Scope Selection */}
                    <div className="font-sync-modal__options">
                        <h3 className="font-sync-modal__options-title">What would you like to do?</h3>
                        
                        <div className="font-sync-modal__option-buttons">
                            <button
                                className={`font-sync-modal__option ${syncScope === 'replace' ? 'font-sync-modal__option--active' : ''}`}
                                onClick={() => setSyncScope('replace')}
                            >
                                <div className="font-sync-modal__option-icon">
                                    <i className="fas fa-exchange-alt"></i>
                                </div>
                                <div className="font-sync-modal__option-content">
                                    <strong>Replace All Fonts</strong>
                                    <p>Update all Hero Header fonts to match new typography</p>
                                </div>
                            </button>

                            <button
                                className={`font-sync-modal__option ${syncScope === 'keep' ? 'font-sync-modal__option--active' : ''}`}
                                onClick={() => setSyncScope('keep')}
                            >
                                <div className="font-sync-modal__option-icon">
                                    <i className="fas fa-lock"></i>
                                </div>
                                <div className="font-sync-modal__option-content">
                                    <strong>Keep Current Fonts</strong>
                                    <p>Save settings without changing Hero Headers</p>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Level Selection (nur wenn Replace ausgewählt und Space Page) */}
                    {syncScope === 'replace' && isSpacePage && (
                        <div className="font-sync-modal__level">
                            <h3 className="font-sync-modal__level-title">Apply changes to:</h3>
                            
                            <div className="font-sync-modal__level-buttons">
                                <button
                                    className={`font-sync-modal__level-btn ${syncLevel === 'page' ? 'font-sync-modal__level-btn--active' : ''}`}
                                    onClick={() => setSyncLevel('page')}
                                >
                                    <i className="fas fa-file"></i>
                                    <span>Current Page</span>
                                </button>

                                <button
                                    className={`font-sync-modal__level-btn ${syncLevel === 'space' ? 'font-sync-modal__level-btn--active' : ''}`}
                                    onClick={() => setSyncLevel('space')}
                                >
                                    <i className="fas fa-layer-group"></i>
                                    <span>Entire Space</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="font-sync-modal__actions">
                    <button 
                        className="font-sync-modal__btn font-sync-modal__btn--cancel"
                        onClick={handleSkip}
                    >
                        Skip
                    </button>
                    <button 
                        className="font-sync-modal__btn font-sync-modal__btn--confirm"
                        onClick={handleConfirm}
                    >
                        {syncScope === 'replace' 
                            ? `Apply ${syncLevel === 'space' && isSpacePage ? '(Space)' : '(Page)'}`
                            : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FontSyncModal;

